<template>
  <div :style="{ height: heightChartWithLegend, width: '100%', position: 'relative' }">
    <horizontal-bar-chart
        :chart-data="datacollection"
        :options="chartOptions"
        :height="null"
        :width="null"
        style="height: 100%; width: 100%;"
        ref="myChart"
        @chart-ready="$emit('chart-ready', $event)"
    ></horizontal-bar-chart>
  </div>
</template>

<script>
import { HorizontalBar, mixins } from 'vue-chartjs'
import ChartDataLabels from 'chartjs-plugin-datalabels'

const HorizontalBarChart = {
  extends: HorizontalBar,
  mixins: [mixins.reactiveProp],
  props: ['options'],
  mounted() {
    this.addPlugin(ChartDataLabels);
    this.addPlugin(LeftAlignedLabelsPlugin);
    this.renderChart(this.chartData, this.options);
    this.$nextTick(() => {
      this.$emit('chart-ready', this.$data._chart);
    });
  }
}

const LeftAlignedLabelsPlugin = {
  id: 'left-aligned-labels',
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    const yAxis = chart.scales['y-axis-0'];
    if (!yAxis) return;

    ctx.save();
    
    const leftMargin = 10;          // Отступ от левого края экрана
    const circleRadius = 11;        // Радиус кружка (диаметр будет 22px)
    const textGap = 10;             // Расстояние от кружка до текста
    
    // Считаем доступную ширину для текста, учитывая кружок и отступы
    const allowedWidth = (yAxis.customWidth || 180) - leftMargin - (circleRadius * 2) - textGap - 15;

    // Массив приятных цветов для кружков (выбираются по первой букве)
    const avatarColors = ['#2980b9', '#27ae60', '#e67e22', '#8e44ad', '#16a085', '#d35400', '#2c3e50'];
    const getCircleColor = (char) => {
      if (!char) return '#bdc3c7';
      const code = char.charCodeAt(0);
      return avatarColors[code % avatarColors.length];
    };

    // Функция обрезки длинного текста
    const truncateText = (text, maxWidth) => {
      ctx.font = "13px RobotoRegular"; // Шрифт для основного текста
      if (ctx.measureText(text).width <= maxWidth) return text;
      let truncated = text;
      while (truncated.length > 0 && ctx.measureText(truncated + '...').width > maxWidth) {
        truncated = truncated.slice(0, -1);
      }
      return truncated + '...';
    };

    yAxis._ticks.forEach((tick, index) => {
      const yPos = yAxis.getPixelForTick(index);
      let rawLabel = tick.label;
      
      // Берем первый символ строки
      const firstChar = rawLabel ? rawLabel.trim().charAt(0).toUpperCase() : '';

      // --- 1. РИСУЕМ КРУЖОК АВАТАРА ---
      const circleX = leftMargin + circleRadius;
      ctx.beginPath();
      ctx.arc(circleX, yPos, circleRadius, 0, Math.PI * 2);
      ctx.fillStyle = getCircleColor(firstChar);
      ctx.fill();

      // --- 2. ПИШЕМ БУКВУ ВНУТРИ КРУЖКА ---
      ctx.font = "13px RobotoRegular";
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      const letterYShift = yPos + 1;
      ctx.fillText(firstChar, circleX, letterYShift);

      // --- 3. РИСУЕМ ТЕКСТ ЛЕЙБЛА СБОКУ ---
      ctx.textAlign = "left";
      ctx.fillStyle = "#494f59"; //
      
      // Координата X, откуда начнется текст (после кружка)
      const textX = leftMargin + (circleRadius * 2) + textGap;

      if (Array.isArray(rawLabel)) {
        const lineHeight = 14;
        const totalHeight = (rawLabel.length - 1) * lineHeight;
        let startY = yPos - totalHeight / 2;

        rawLabel.forEach(line => {
          ctx.font = "13px RobotoRegular"; // Возвращаем шрифт для ФИО
          ctx.textBaseline = "middle";
          const fitLine = truncateText(line, allowedWidth);
          ctx.fillText(fitLine, textX, startY);
          startY += lineHeight;
        });
      } else {
        ctx.font = "13px RobotoRegular";   // Возвращаем шрифт для ФИО
        ctx.textBaseline = "middle";
        const fitLabel = truncateText(rawLabel, allowedWidth);
        ctx.fillText(fitLabel, textX, yPos);
      }
    });

    ctx.restore();
  }
};

export default {
  name: "BarStackChartJS",
  components: { HorizontalBarChart },
  props: {
    datasource: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    heightChart: { type: Number, default: 400 },
    showLegend: { type: Boolean, default: true },
    barThickness: { type: Number, default: 20 },
    wordWrap: { type: Boolean, default: false },
    maxValue: { type: Number, default: null },
    yAxisTickLabelWidth: { type: Number, default: 180 },
    click: Function
  },
  computed: {
    heightChartWithLegend() {
      const count = this.categories.length;
      if (count === 0) return '100px';

      const BAR_HEIGHT = this.barThickness; // Высота полоски (совпадает с barThickness)
      const GAP_HEIGHT = 4; // Фиксированный отступ между полосками в пикселях

      // В режиме offset: true высота одной строки сетки — это BAR + GAP
      const rowHeight = BAR_HEIGHT + GAP_HEIGHT;

      // Высота всех строк + один GAP на внешние поля холста (чтобы верхний бар не резался)
      const contentHeight = (rowHeight * count) + GAP_HEIGHT;

      // Добавляем запас на легенду, если она включена
      const legendSpace = this.showLegend ? 45 : 0;

      return (contentHeight + legendSpace) + 'px';
    },
    datacollection() {
      return {
        labels: this.categories,
        datasets: this.datasource.map((item) => ({
          label: item.name || '',
          backgroundColor: item.color || '#3498db',
          data: item.data,
          // Сохраняем кастомные значения для форматтера, если они есть
          ids: item.ids || [],
          customValues: item.value || []
        }))
      }
    },
    chartOptions() {
      const yAxisWidth = this.yAxisTickLabelWidth || 170;
      return {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements) => {
            if (!elements.length) return

            const chartInstance = this.$refs.myChart.$data._chart;

            if (!chartInstance) {
                console.log('chartInstance not found');
                return;
            }

            const activeElement = chartInstance.getElementAtEvent(event)[0]
            if (!activeElement) {
                console.log('activeElement not found');
                return
            }

            const index = activeElement._index
            const datasetIndex = activeElement._datasetIndex

            this.click(event, null, { dataPointSelection: index, seriesIndex: datasetIndex });
        },
        scales: {
          xAxes: [{
            stacked: true, // Включаем стек
            display: false, // В Apex xaxis.labels.show: false
            ticks: {
              beginAtZero: true,
              max: this.maxValue || undefined
            },
            gridLines: { display: false },
          }],
          yAxes: [{
            stacked: true, // Включаем стек
            display: !!this.categories.length,
            barThickness: this.barThickness,
            /*maxBarThickness: this.barThickness,*/
            categoryPercentage: 1.0,
            barPercentage: 1.0,
            offset: true,
            ticks: {
              display: false,
              /*fontFamily: 'RobotoRegular',
              fontSize: 13,
              fontColor: '#494f59',*/
              /*callback: (value) => {
                if (!this.wordWrap) return  value;
                return this.wrapLabel(value, 30)
              }*/
            },
            gridLines: { display: false },
            afterFit: function(scaleInstance) {
              scaleInstance.width = yAxisWidth;
              scaleInstance.customWidth = yAxisWidth;
            }
          }]
        },
        legend: {
          display: this.showLegend,
          position: 'bottom',
          labels: {
            usePointStyle: false,
            padding: 15,
            fontFamily: 'RobotoRegular',
            boxWidth: 12,
            fontSize: 12
          }
        },
        tooltips: {
          enabled: true,
          mode: 'y',         // Ловим всю строку по вертикали
          intersect: false,  // Срабатывает наведение по всей строке
          
          // Настройки шрифтов
          titleFontFamily: 'RobotoRegular',
          titleFontSize: 13,
          titleFontStyle: 'normal',
          titleFontColor: '#ffffff',
          titleMarginBottom: 4, // Отступ от полного имени до списка секций

          // Настройки футера для итога
          footerFontFamily: 'RobotoRegular',
          footerFontSize: 13,
          footerFontStyle: 'normal', // Сделаем итог жирным для выделения
          footerFontColor: '#ffffff',
          footerMarginTop: 4,      // Отступ сверху перед футером

          callbacks: {
            // 1. В ТАЙТЛ СУЕМ ПОЛНОЕ НАЗВАНИЕ СТРОКИ ИЗ ПЛАГИНА
            title: (tooltipItems, data) => {
              if (!tooltipItems.length) return '';
              
              const chartInstance = this.$refs.myChart.$data._chart;
              if (!chartInstance) return '';

              const yAxis = chartInstance.scales['y-axis-0'];
              if (!yAxis || !yAxis._ticks) return '';

              const rowIndex = tooltipItems[0].index;
              const tick = yAxis._ticks[rowIndex];

              return tick && tick.fullLabelForTooltip ? tick.fullLabelForTooltip : data.labels[rowIndex];
            },

            // 2. В ЛЕЙБЛЕ ОСТАВЛЯЕМ ТОЛЬКО СЕКЦИИ БАРОВ
            label: (tooltipItem, data) => {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const value = dataset.customValues[tooltipItem.index] || tooltipItem.xLabel;
              return ` ${dataset.label}: ${value}`;
            },

            // 3. ДОБАВЛЯЕМ СУММУ СТЕКА В ФУТЕР
            footer: (tooltipItems, data) => {
              if (!tooltipItems.length) return '';
              
              // Берем индекс строки из первого элемента тултипа
              const rowIndex = tooltipItems[0].index;
              
              const total = data.datasets.reduce((sum, dataset) => {
                // Пытаемся взять значение из кастомных, если его нет — из дефолтных данных
                const rawVal = (dataset.customValues && dataset.customValues[rowIndex] !== undefined)
                  ? dataset.customValues[rowIndex]
                  : dataset.data[rowIndex];
                
                // Очищаем значение (на случай если там строки вида "7 шт" или просто строки)
                const numberValue = parseInt(rawVal, 10);
                
                return sum + (!isNaN(numberValue) ? numberValue : 0);
              }, 0);

              return `Итого: ${total}`;
            }
          },
          
          position: 'average',
          custom: function(tooltipModel) {
            if (tooltipModel.opacity === 0) return;
            
            const chartInstance = this._chart; 
            if (!chartInstance) return;

            if (tooltipModel.caretX < yAxisWidth) {
              tooltipModel.caretX = yAxisWidth + 10;
              tooltipModel.x = yAxisWidth + 10;
            }

            const chartWidth = chartInstance.width;
            const tooltipWidth = tooltipModel.width;
            const paddingRight = 10;

            if (tooltipModel.x + tooltipWidth > chartWidth - paddingRight) {
              const newX = chartWidth - tooltipWidth - paddingRight;
              tooltipModel.x = newX;
              tooltipModel.caretX = Math.max(newX + 10, Math.min(tooltipModel.caretX, chartWidth - paddingRight));
            }
          }
        },
        plugins: {
          datalabels: {
            // Скрываем проценты/цифры в углу, если значение 0
            display: (ctx) => {
              const val = ctx.dataset.data[ctx.dataIndex];
              return val > 0; 
            },
            color: '#fff', // В стековых барах обычно текст белый на цветном фоне
            font: {
              family: 'RobotoRegular',
              size: 14
            },
            formatter: (value, ctx) => {
              // Если есть поле value (как в твоем Apex), берем его, иначе данные
              const customVal = ctx.dataset.customValues[ctx.dataIndex];
              return customVal !== undefined ? customVal : value;
            }
          }
        }
      }
    }
  },
  methods: {
    wrapLabel(value, maxLen = 12) {
      if (!value || typeof value !== 'string') return value;

      const words = value.split(' ');
      const lines = [];
      let current = '';

      words.forEach(word => {
        if ((current + ' ' + word).trim().length > maxLen) {
          lines.push(current.trim());
          current = word;
        } else {
          current += ' ' + word;
        }
      });

      if (current) lines.push(current.trim());

      return lines;
    }
  }
}
</script>